import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import stockpileService from '../../services/stockpileService';
import StockpileDetailModal from './StockpileDetailModal';
import './StockpileDashboard.css';

const RANGE_OPTIONS = [
    { value: 7, label: '7d' },
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
];

// Debounce search input so we don't spam the dashboard endpoint on every keystroke.
const SEARCH_DEBOUNCE_MS = 300;

const formatNumberFull = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const unitDisplay = (row) => row.unit_name || row.unit_code || '';

const StockpileDashboard = ({ category = null, productType = null }) => {
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overview, setOverview] = useState(null);
    // Row click opens the same big detail modal used by the variant card,
    // not an inline expander. Keeps UX consistent across the two entry points.
    const [selectedVariant, setSelectedVariant] = useState(null);

    // searchInput is what the user types; searchQuery is the debounced value
    // we actually send to the backend.
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchQuery(searchInput.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchInput]);

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await stockpileService.getDashboard({
                days,
                category,
                productType,
                q: searchQuery || undefined,
            });
            setOverview(data);
        } catch (err) {
            console.error('Stockpile dashboard load failed', err);
            setError('Could not load stockpile dashboard.');
        } finally {
            setLoading(false);
        }
    }, [days, category, productType, searchQuery]);

    useEffect(() => { refresh(); }, [refresh]);

    const hasActiveFilters = Boolean(category || productType || searchQuery);

    /**
     * Build the minimal variant-shaped object the StockpileDetailModal expects
     * from a dashboard row. The modal pulls the live balance + movements +
     * series itself, so we only need the identifying fields and unit display
     * info for header rendering and the "+ Inbound" / "− Outbound" buttons.
     *
     * The modal checks `Boolean(variant.unit)` to enable the action buttons;
     * if a variant is in the dashboard at all it has at least one movement,
     * which means it must have a unit set, so we synthesize a truthy unit
     * marker from `unit_code` (falling back to a sentinel).
     */
    const rowToVariant = (row) => ({
        id: row.variant_id,
        shortname: row.shortname || '',
        unit: row.unit_code || row.unit_name || 'unit',
        unit_code: row.unit_code || null,
        unit_name: row.unit_name || null,
        base_stock_item_data: row.stock_item_name
            ? { dynamic_name: row.stock_item_name, name: row.stock_item_name }
            : null,
    });

    const totals = overview?.totals;
    const variants = overview?.variants || [];
    const netClass = totals && Number(totals.net) >= 0 ? 'positive' : 'negative';

    return (
        <div className="stockpile-dashboard">
            <div className="stockpile-dashboard-controls">
                <div className="stockpile-search-wrap">
                    <FontAwesomeIcon icon={faSearch} className="stockpile-search-icon" />
                    <input
                        type="text"
                        className="stockpile-search-input"
                        placeholder="Search variant or product name…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {searchInput && (
                        <button
                            type="button"
                            className="stockpile-search-clear"
                            onClick={() => setSearchInput('')}
                            aria-label="Clear search"
                            title="Clear search"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
                <span className="label">Activity window</span>
                {RANGE_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        className={`stockpile-range-button ${days === opt.value ? 'active' : ''}`}
                        onClick={() => setDays(opt.value)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {loading && !overview ? (
                <div className="stockpile-loading-state">Loading stockpile activity…</div>
            ) : error ? (
                <div className="stockpile-loading-state">{error}</div>
            ) : variants.length === 0 ? (
                <div className="stockpile-empty-state">
                    {hasActiveFilters ? (
                        <>
                            <h3>No stockpile rows match your filters</h3>
                            <p>
                                Try a different category, subtype, or search term — or clear
                                them to see everything in the ledger.
                            </p>
                        </>
                    ) : (
                        <>
                            <h3>No stockpile activity yet</h3>
                            <p>
                                Movements will appear here once stock is on hand. Open any product
                                variant to set an opening balance, or finalise a Bill of Lading on
                                a delivery to auto-post inbound stock.
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <div className="stockpile-summary-grid">
                        <div className="stockpile-summary-tile">
                            <div className="label">Variants stocked</div>
                            <div className="value">{totals.variant_count}</div>
                        </div>
                        <div className="stockpile-summary-tile inbound">
                            <div className="label">Inbound (last {days}d)</div>
                            <div className="value">{formatNumberFull(totals.inbound)}</div>
                        </div>
                        <div className="stockpile-summary-tile outbound">
                            <div className="label">Outbound (last {days}d)</div>
                            <div className="value">{formatNumberFull(totals.outbound)}</div>
                        </div>
                        <div className="stockpile-summary-tile net">
                            <div className="label">Net change ({days}d)</div>
                            <div className={`value ${netClass}`}>
                                {Number(totals.net) >= 0 ? '+' : ''}{formatNumberFull(totals.net)}
                            </div>
                        </div>
                    </div>

                    <div className="stockpile-table-wrap">
                        <table className="stockpile-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th className="numeric">On hand</th>
                                    <th className="numeric">Inbound ({days}d)</th>
                                    <th className="numeric">Outbound ({days}d)</th>
                                    <th className="numeric">Net ({days}d)</th>
                                    <th>Last movement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {variants.map((row) => {
                                    const unit = unitDisplay(row);
                                    return (
                                        <tr
                                            key={row.variant_id}
                                            className="expandable"
                                            onClick={() => setSelectedVariant(rowToVariant(row))}
                                        >
                                            <td>
                                                <div className="product-cell">
                                                    <span className="primary">{row.shortname || '(no shortname)'}</span>
                                                    <span className="secondary">{row.stock_item_name}</span>
                                                </div>
                                            </td>
                                            <td className="numeric">
                                                {formatNumberFull(row.on_hand)} {unit}
                                            </td>
                                            <td className="numeric delta-positive">
                                                {Number(row.inbound_window) > 0 ? '+' : ''}{formatNumberFull(row.inbound_window)}
                                            </td>
                                            <td className="numeric delta-negative">
                                                {Number(row.outbound_window) > 0 ? '−' : ''}{formatNumberFull(row.outbound_window)}
                                            </td>
                                            <td className={`numeric ${Number(row.net_window) >= 0 ? 'delta-positive' : 'delta-negative'}`}>
                                                {Number(row.net_window) >= 0 ? '+' : ''}{formatNumberFull(row.net_window)}
                                            </td>
                                            <td className="last-movement">
                                                {formatDate(row.last_movement_at)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <StockpileDetailModal
                isOpen={Boolean(selectedVariant)}
                variant={selectedVariant}
                onClose={() => setSelectedVariant(null)}
                onChanged={() => {
                    // Movements posted from the modal can shift on-hand, inbound/outbound
                    // totals and last-movement timestamps in the dashboard; pull fresh
                    // numbers so the user sees their action reflected immediately.
                    refresh();
                }}
            />
        </div>
    );
};

export default StockpileDashboard;
